import React, { useEffect, useState, useCallback } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  getAllFeatureToggles,
  createFeatureToggle,
  updateFeatureToggle,
  deleteFeatureToggle,
  FeatureToggle,
  CreateFeatureToggleDto
} from '../../services/FeatureToggle';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Edit, ArrowUpDown, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const FeatureTogglesPage: React.FC = () => {
  const [toggles, setToggles] = useState<FeatureToggle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editingToggle, setEditingToggle] = useState<FeatureToggle | null>(null);

  // Form state
  const [formKey, setFormKey] = useState<string>('');
  const [formName, setFormName] = useState<string>('');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formIsEnabled, setFormIsEnabled] = useState<boolean>(false);
  const [formIsEnabledForQueri, setFormIsEnabledForQueri] = useState<boolean>(false);

  // Error state
  const [keyError, setKeyError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const fetchToggles = useCallback(async () => {
    setLoading(true);
    const response = await getAllFeatureToggles();
    if (!response.success || !response.data) {
      setLoading(false);
      return;
    }
    setToggles(response.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchToggles();
  }, [fetchToggles]);

  const resetForm = () => {
    setFormKey('');
    setFormName('');
    setFormDescription('');
    setFormIsEnabled(false);
    setFormIsEnabledForQueri(false);
    setKeyError(null);
    setNameError(null);
    setGeneralError(null);
  };

  const handleOpenCreateDialog = () => {
    resetForm();
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    resetForm();
  };

  const handleOpenEditDialog = (toggle: FeatureToggle) => {
    setEditingToggle(toggle);
    setFormKey(toggle.key);
    setFormName(toggle.name);
    setFormDescription(toggle.description || '');
    setFormIsEnabled(toggle.isEnabled);
    setFormIsEnabledForQueri(toggle.isEnabledForQueri);
    setKeyError(null);
    setNameError(null);
    setGeneralError(null);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingToggle(null);
    resetForm();
  };


  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setKeyError(null);
    setNameError(null);
    setGeneralError(null);

    const dto: CreateFeatureToggleDto = {
      key: formKey,
      name: formName,
      description: formDescription || undefined,
      isEnabled: formIsEnabled,
      isEnabledForQueri: formIsEnabledForQueri,
    };

    try {
      const response = await createFeatureToggle(dto);

      if (!response.success && response.errors) {
        const keyErrorObj = response.errors.find((err) => err?.field === 'key');
        const nameErrorObj = response.errors.find((err) => err?.field === 'name');

        if (keyErrorObj && keyErrorObj.detail) {
          setKeyError(keyErrorObj.detail);
        }
        if (nameErrorObj && nameErrorObj.detail) {
          setNameError(nameErrorObj.detail);
        }
        if (!keyErrorObj && !nameErrorObj && response.errors[0]) {
          setGeneralError(response.errors[0].title || response.errors[0].detail || null);
        }
        return;
      }

      toast.success('Feature toggle created successfully!');
      handleCloseCreateDialog();
      fetchToggles();
    } catch (err) {
      setGeneralError('An error occurred. Please try again.');
    }
  };

  const handleEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingToggle) return;

    setNameError(null);
    setGeneralError(null);

    try {
      const response = await updateFeatureToggle(editingToggle.key, {
        name: formName,
        description: formDescription || undefined,
        isEnabled: formIsEnabled,
        isEnabledForQueri: formIsEnabledForQueri,
      });

      if (!response.success && response.errors) {
        const nameErrorObj = response.errors.find((err) => err?.field === 'name');

        if (nameErrorObj && nameErrorObj.detail) {
          setNameError(nameErrorObj.detail);
        } else if (response.errors[0]) {
          setGeneralError(response.errors[0].title || response.errors[0].detail || null);
        }
        return;
      }

      toast.success('Feature toggle updated successfully!');
      handleCloseEditDialog();
      fetchToggles();
    } catch (err) {
      setGeneralError('An error occurred. Please try again.');
    }
  };

  const handleToggleEnabled = async (toggle: FeatureToggle, scope: 'all' | 'queri') => {
    try {
      const updateDto = scope === 'queri'
        ? { isEnabledForQueri: !toggle.isEnabledForQueri }
        : { isEnabled: !toggle.isEnabled };

      const response = await updateFeatureToggle(toggle.key, updateDto);

      if (!response.success) {
        toast.error('Failed to update feature toggle');
        return;
      }

      const newState = scope === 'queri' ? !toggle.isEnabledForQueri : !toggle.isEnabled;
      const scopeName = scope === 'queri' ? 'Queri' : 'all organizations';
      toast.success(`Feature toggle ${newState ? 'enabled' : 'disabled'} for ${scopeName}!`);
      fetchToggles();
    } catch (err) {
      toast.error('An error occurred while updating the toggle');
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm('Are you sure you want to delete this feature toggle?')) {
      return;
    }

    try {
      const response = await deleteFeatureToggle(key);

      if (!response.success) {
        toast.error('Failed to delete feature toggle');
        return;
      }

      toast.success('Feature toggle deleted successfully!');
      fetchToggles();
    } catch (err) {
      toast.error('An error occurred while deleting the toggle');
    }
  };

  // Define table columns
  const columns: ColumnDef<FeatureToggle>[] = [
    {
      accessorKey: 'key',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="font-mono"
          >
            Key
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div className="font-mono">{row.getValue('key')}</div>,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue('name')}</div>,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const description = row.getValue('description') as string | null;
        return (
          <div className="max-w-[300px]">
            {description || <span className="text-muted-foreground">-</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'isEnabled',
      header: 'All Orgs',
      cell: ({ row }) => {
        const isEnabled = row.getValue('isEnabled') as boolean;
        return (
          <Badge variant={isEnabled ? 'default' : 'secondary'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'isEnabledForQueri',
      header: 'Queri',
      cell: ({ row }) => {
        const isEnabled = row.getValue('isEnabledForQueri') as boolean;
        return (
          <Badge variant={isEnabled ? 'default' : 'secondary'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'updatedBy',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Updated By
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue('updatedBy')}</div>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const toggle = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-12">All:</span>
                <Switch
                  checked={toggle.isEnabled}
                  onCheckedChange={() => handleToggleEnabled(toggle, 'all')}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-12">Queri:</span>
                <Switch
                  checked={toggle.isEnabledForQueri}
                  onCheckedChange={() => handleToggleEnabled(toggle, 'queri')}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleOpenEditDialog(toggle)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(toggle.key)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create table instance
  const table = useReactTable({
    data: toggles,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  if (loading) {
    return (
      <div className="flex justify-center mt-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Feature Toggles</h1>

      <Button onClick={handleOpenCreateDialog} className="mb-4">
        Create Feature Toggle
      </Button>

      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Feature Toggle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-key">Key</Label>
                <Input
                  id="create-key"
                  value={formKey}
                  onChange={(e) => setFormKey(e.target.value)}
                  className={keyError ? 'border-destructive' : ''}
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  {keyError || 'Use kebab-case format (e.g., new-feature)'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-name">Name</Label>
                <Input
                  id="create-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={nameError ? 'border-destructive' : ''}
                  required
                />
                {nameError && (
                  <p className="text-xs text-destructive">{nameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-description">Description</Label>
                <Textarea
                  id="create-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="create-enabled">Enabled for All Organizations</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable this feature for all organizations
                    </p>
                  </div>
                  <Switch
                    id="create-enabled"
                    checked={formIsEnabled}
                    onCheckedChange={setFormIsEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="create-enabled-queri">Enabled for Queri</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable specifically for Queri organization
                    </p>
                  </div>
                  <Switch
                    id="create-enabled-queri"
                    checked={formIsEnabledForQueri}
                    onCheckedChange={setFormIsEnabledForQueri}
                  />
                </div>
              </div>
              {generalError && (
                <p className="text-sm text-destructive">{generalError}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseCreateDialog}>
                Cancel
              </Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Feature Toggle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-key">Key</Label>
                <Input
                  id="edit-key"
                  value={formKey}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={nameError ? 'border-destructive' : ''}
                  required
                />
                {nameError && (
                  <p className="text-xs text-destructive">{nameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="edit-enabled">Enabled for All Organizations</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable this feature for all organizations
                    </p>
                  </div>
                  <Switch
                    id="edit-enabled"
                    checked={formIsEnabled}
                    onCheckedChange={setFormIsEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="edit-enabled-queri">Enabled for Queri</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable specifically for Queri organization
                    </p>
                  </div>
                  <Switch
                    id="edit-enabled-queri"
                    checked={formIsEnabledForQueri}
                    onCheckedChange={setFormIsEnabledForQueri}
                  />
                </div>
              </div>
              {generalError && (
                <p className="text-sm text-destructive">{generalError}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseEditDialog}>
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Table with filters and column visibility */}
      <div className="w-full">
        <div className="flex items-center py-4 gap-2">
          <Input
            placeholder="Filter by name..."
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Card>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <p className="text-muted-foreground">
                        No feature toggles found. Create one to get started!
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FeatureTogglesPage;
